import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { ArrowLeft, Shield, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function SettingsPage() {
  const { user, setup2FA, enable2FA } = useAuth();
  const navigate = useNavigate();

  const [setupData, setSetupData] = useState(null);
  const [otpValue, setOtpValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [enabling, setEnabling] = useState(false);

  const handleSetup2FA = async () => {
    setLoading(true);
    try {
      const data = await setup2FA();
      setSetupData(data);
    } catch (error) {
      toast.error('Kon 2FA niet instellen');
    } finally {
      setLoading(false);
    }
  };

  const handleEnable2FA = async () => {
    if (otpValue.length !== 6) return;
    setEnabling(true);
    try {
      await enable2FA(otpValue);
      toast.success('2FA succesvol ingeschakeld!');
      setSetupData(null);
      setOtpValue('');
    } catch (error) {
      toast.error('Ongeldige code');
      setOtpValue('');
    } finally {
      setEnabling(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-card/80 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold">Instellingen</h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {/* Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card>
            <CardHeader>
              <CardTitle>Profiel</CardTitle>
              <CardDescription>Je accountgegevens</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-2xl font-bold">
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h3 className="font-semibold text-lg">{user?.name}</h3>
                  <p className="text-muted-foreground">{user?.email}</p>
                  <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs bg-secondary capitalize">
                    {user?.role}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* 2FA Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-primary" />
                <div>
                  <CardTitle>Twee-factor authenticatie</CardTitle>
                  <CardDescription>
                    Voeg een extra beveiligingslaag toe aan je account
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {user?.totp_enabled ? (
                <div className="flex items-center gap-3 p-4 rounded-xl bg-success/10 text-success">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">2FA is ingeschakeld</span>
                </div>
              ) : setupData ? (
                <div className="space-y-6">
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground mb-4">
                      Scan deze QR-code met je authenticator app (bijv. Google Authenticator)
                    </p>
                    <img
                      src={setupData.qr_code}
                      alt="2FA QR Code"
                      className="mx-auto w-48 h-48 rounded-xl"
                      data-testid="2fa-qr-code"
                    />
                    <p className="text-xs text-muted-foreground mt-4">
                      Of voer deze code handmatig in:
                      <br />
                      <span className="font-mono font-medium">{setupData.secret}</span>
                    </p>
                  </div>

                  <div className="space-y-4">
                    <p className="text-sm text-center">Voer de 6-cijferige code in om te bevestigen:</p>
                    <div className="flex justify-center">
                      <InputOTP
                        maxLength={6}
                        value={otpValue}
                        onChange={setOtpValue}
                        data-testid="2fa-verify-input"
                      >
                        <InputOTPGroup>
                          <InputOTPSlot index={0} />
                          <InputOTPSlot index={1} />
                          <InputOTPSlot index={2} />
                          <InputOTPSlot index={3} />
                          <InputOTPSlot index={4} />
                          <InputOTPSlot index={5} />
                        </InputOTPGroup>
                      </InputOTP>
                    </div>
                    <Button
                      onClick={handleEnable2FA}
                      disabled={otpValue.length !== 6 || enabling}
                      className="w-full rounded-full"
                      data-testid="enable-2fa-btn"
                    >
                      {enabling ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        '2FA Inschakelen'
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  onClick={handleSetup2FA}
                  disabled={loading}
                  className="w-full rounded-full"
                  data-testid="setup-2fa-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Shield className="w-4 h-4 mr-2" />
                  )}
                  2FA Instellen
                </Button>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
}
